import {Alert, Box, Inline, Stack, Text} from 'epic-fortnite-react';
import {isEmpty} from 'epic-fortnite-react/utils/isEmpty';
import moment from 'moment';
import React, {useState} from 'react';
import styled from 'styled-components';

import {usePublishedIslands} from '../submit-island.hooks';
import {SubmitIslandPageData} from '../types';

import {IslandFormModal} from './IslandFormModal';
import {ModerationStatus} from './ModerationStatus';

interface SkeletonProps {
  $height?: number;
  $width?: number;
}

const StyledBox = styled(Box)`
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 2px;

  &:hover,
  &.focus-visible {
    background-color: #fff;
  }

  &.focus-visible {
    box-shadow: 0 0 0 5px #ff6305;
  }
`;

/* todo: make this a better reusable component */
const Skeleton = styled.div<SkeletonProps>`
  border-radius: 999px;
  background-color: #ccc;
  flex-shrink 1;
  max-width: 100%;
  width: ${({$width}) => ($width ? `${$width}px` : '1px')} !important;
  height: ${({$height}) => ($height ? `${$height}px` : '1px')} !important;
`;

const LoadingIsland = () => {
  return (
    <StyledBox size="med">
      <Stack gap="sm">
        <Inline flex="start" gap="sm" justify="space-between">
          <Skeleton $width={300} $height={12} />
          <Skeleton $width={200} $height={8} />
        </Inline>
        <Inline flex="start" gap="sm" justify="space-between">
          <Skeleton $width={100} $height={12} />
          <Skeleton $width={50} $height={8} />
        </Inline>
      </Stack>
    </StyledBox>
  );
};

interface Props {
  email: string;
  translations: SubmitIslandPageData;
  submissionType?: string;
}

export const SelectIsland = ({email, translations, submissionType}: Props) => {
  const {data, mutate} = usePublishedIslands();

  const islands = data?.islands ?? [];
  const config = data?.config;

  const hasExceededPendingLimit = React.useMemo(() => {
    if (!islands) {
      return false;
    }

    if (config?.multiSubmissionsEnabled) {
      let pendingSubmissionCount = 0;
      for (let i = 0; i < islands.length; i++) {
        if (islands[i].moderationStatus === 'PENDING') {
          console.log('pending++');
          pendingSubmissionCount++;
        }
      }

      return pendingSubmissionCount >= (config.pendingSubmissionsLimit ?? 1);
    }

    for (let i = 0; i < islands.length; i++) {
      if (islands[i].moderationStatus === 'PENDING') {
        return true;
      }
    }
    return false;
  }, [config?.multiSubmissionsEnabled, config?.pendingSubmissionsLimit, islands]);

  const isLoading = !islands;

  const [selectedIsland, setSelectedIsland] = useState<string | undefined>(undefined);

  return (
    <>
      <Stack gap="3xl">
        <Box backgroundColor="#0B85EC" size="xl" textAlign="left">
          <Stack gap="lg">
            <Text color="#fff" size="xl" transform="uppercase">
              {translations?.selectIsland?.title}
            </Text>
            <Stack gap="xs">
              {isLoading ? (
                <>
                  <LoadingIsland />
                  <LoadingIsland />
                  <LoadingIsland />
                </>
              ) : (
                <>
                  {isEmpty(islands) ? (
                    // use Alert when available
                    <Alert>{translations?.selectIsland?.noIslandsAlert}</Alert>
                  ) : (
                    <>
                      {islands?.map((island, i) => {
                        return (
                          <React.Fragment key={island.code}>
                            <StyledBox
                              key={i}
                              onClick={() => setSelectedIsland(island.code)}
                              size="med">
                              <Stack gap="sm">
                                <Inline flex="start" gap="sm" justify="space-between">
                                  <Text size="med" transform="uppercase">
                                    {island.title}
                                  </Text>
                                  {island.moderationStatus !== 'UNMODERATED' ? (
                                    <ModerationStatus
                                      moderationStatus={island.moderationStatus}
                                      text={island.moderationStatus}
                                    />
                                  ) : island.submittedVersion ? (
                                    <ModerationStatus
                                      moderationStatus={island.submittedVersion.moderationStatus}
                                      text={`v${island.submittedVersion.version}: ${island.submittedVersion.moderationStatus}`}
                                    />
                                  ) : null}
                                </Inline>
                                <Inline flex="start" gap="sm" justify="space-between">
                                  <Text color="#666" size="med">
                                    {island.code}
                                  </Text>
                                  <Text color="#666" font="OpenSans" size="sm">
                                    {moment(island.created).format('ll')} -{' '}
                                    {translations?.selectIsland?.islandVersion.replace(
                                      '{version}',
                                      `${island.version}`
                                    )}
                                  </Text>
                                </Inline>
                              </Stack>
                            </StyledBox>
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Box>
      </Stack>
      <IslandFormModal
        translations={translations}
        isOpen={selectedIsland !== undefined}
        onDismiss={() => {
          setSelectedIsland(undefined);
          mutate(); // trigger islands list refetch
        }}
        submissionType={submissionType}
        island={islands?.find((island) => island.code === selectedIsland)}
        hasExceededPendingLimit={hasExceededPendingLimit}
        email={email}
      />
    </>
  );
};
